import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Layout, PageHeader } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2 } from "lucide-react";

const empty = { name: "", description: "", weight: 3 };

export default function CriteriaPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const { isAdmin } = useAuth();

  const load = async () => { const { data } = await api.get("/criteria"); setItems(data); setLoading(false); };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (c) => { setForm({ name: c.name, description: c.description || "", weight: c.weight }); setEditId(c.id); setOpen(true); };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Nome obbligatorio"); return; }
    try {
      if (editId) await api.put(`/criteria/${editId}`, form);
      else await api.post("/criteria", form);
      toast.success("Criterio salvato");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare questo criterio?")) return;
    await api.delete(`/criteria/${id}`);
    toast.success("Criterio eliminato"); load();
  };

  return (
    <Layout>
      <PageHeader title="Criteri di valutazione" subtitle={`${items.length} criteri · peso 1–5`}>
        {isAdmin && <Button data-testid="add-criterion-btn" onClick={openNew} className="rounded-none"><Plus className="w-4 h-4 mr-2" /> Nuovo criterio</Button>}
      </PageHeader>

      <div className="p-8">
        {loading ? <div className="flex justify-center py-24"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div> : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="criteria-list">
            {items.map((c) => (
              <div key={c.id} className="bg-white border border-border p-5" data-testid={`criterion-${c.name}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-heading font-semibold tracking-tight">{c.name}</h3>
                    {c.is_default && <span className="data-label">Predefinito</span>}
                  </div>
                  <div className="text-right">
                    <div className="font-mono font-bold text-2xl text-primary">{c.weight}</div>
                    <div className="data-label">peso</div>
                  </div>
                </div>
                {c.description && <p className="text-sm text-muted-foreground mt-3">{c.description}</p>}
                {isAdmin && (
                  <div className="flex gap-3 mt-4 pt-4 border-t border-border">
                    <button onClick={() => openEdit(c)} className="text-sm text-muted-foreground hover:text-primary flex items-center gap-1"><Pencil className="w-3.5 h-3.5" /> Modifica</button>
                    <button onClick={() => remove(c.id)} className="text-sm text-muted-foreground hover:text-destructive flex items-center gap-1"><Trash2 className="w-3.5 h-3.5" /> Elimina</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none">
          <DialogHeader><DialogTitle className="font-heading tracking-tight">{editId ? "Modifica criterio" : "Nuovo criterio"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label className="data-label">Nome *</Label><Input data-testid="criterion-name-input" className="mt-1 rounded-none" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><Label className="data-label">Descrizione</Label><Input className="mt-1 rounded-none" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div>
              <div className="flex items-center justify-between"><Label className="data-label">Peso</Label><span className="font-mono font-semibold">{form.weight}</span></div>
              <Slider className="mt-2" value={[form.weight]} min={1} max={5} step={1} onValueChange={([v]) => setForm({ ...form, weight: v })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setOpen(false)}>Annulla</Button>
            <Button data-testid="save-criterion-btn" className="rounded-none" onClick={save}>Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
